import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { Editor } from "@tiptap/core";
import { defer, keyBy, last, throttle } from "lodash";
import {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  ChatbotRoleEnum,
  CreateAskAiMessageMutationMutation,
  CreateAskAiMessageMutationMutationVariables,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
  RetryAskAiMessageMutationMutation,
  RetryAskAiMessageMutationMutationVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import getAskAiChatSessionHistoryQuery from "@apps/ask-ai/graphql/get-ask-ai-chat-session-history-query";
import useLabel from "@apps/use-label/use-label";
import cache from "@cache/cache";
import { Tooltip } from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers.tsx";
import { getWatchedQuery } from "@graphql/client";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";

import {
  ChatSessionContext,
  chatSessionLoadingTextVar,
} from "./ask-ai-context";
import AskAIEmptyPrompts from "./components/ask-ai-empty-prompts";
import AskAIErrorMessage from "./components/ask-ai-error-message";
import AskAIForm from "./components/ask-ai-form";
import AskAILoading from "./components/ask-ai-loading";
import AskAIMessage from "./components/ask-ai-message";
import createAskAiMessageMutation from "./graphql/create-ask-ai-message-mutation";
import getAskAIChatSessionQuery from "./graphql/get-ask-ai-session-query";
import retryAskAiMessageMutation from "./graphql/retry-ask-ai-message-mutation";
import { createChatMessageNode } from "./helpers";

export type ChatSessionFunctionCallType = {
  chatSessionId: number;
  functionName: string;
  parameters?: any;
} | null;

export type ChatSessionContextValue = {
  assessmentId?: number;
  assessmentQuestionId?: number;
  meetingId?: number;
  currentSuggestedAssessmentResponseId?: string | null;
  setCurrentSuggestedAssessmentResponseId?: (id: string | null) => void;
};

const AskAI = ({
  chatSessionId,
  onChangeChatSessionId,
  onStartChatSession,
  className,
  isFullScreen,
  formButtons,
  totalYPaddingAroundContainer,
  isInSidebar = false,
}: {
  chatSessionId: number | null;
  onChangeChatSessionId: (chatSessionId: number | null) => void;
  onStartChatSession: () => void;
  className?: string;
  isFullScreen: boolean;
  totalYPaddingAroundContainer: number;
  formButtons?: {
    text: string;
    prompt: string;
    icon: TopicflowIconType;
  }[];
  isInSidebar?: boolean;
}) => {
  const chatSessionLoadingText = useReactiveVar(chatSessionLoadingTextVar);

  const location = useLocation<TFLocationState>();

  const lastUserMessageRef = useRef<null | HTMLDivElement>(null);
  const editorRef = useRef<null | Editor>(null);
  const scrollRef = useRef<null | HTMLDivElement>(null);
  const {
    assessmentId,
    assessmentQuestionId,
    meetingId,
    currentSuggestedAssessmentResponseId,
  } = useContext(ChatSessionContext);

  const [inputMessage, setInputMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0); // used to force re-render the loading/error UI
  const [
    inputMessageWhileCreatingSession,
    setInputMessageWhileCreatingSession,
  ] = useState("");
  const hasUsedSystemContextMessageRef = useRef(false);
  const pendingSystemContextMessageRef = useRef(false);

  const [
    retryAskAiMessage,
    { loading: loadingRetryAskAiMessage, called: calledRetryAskAiMessage },
  ] = useMutation<
    RetryAskAiMessageMutationMutation,
    RetryAskAiMessageMutationMutationVariables
  >(retryAskAiMessageMutation);

  const [
    createAskAIMessage,
    {
      loading: loadingCreateMessage,
      error: errorCreateMessage,
      called: calledCreateAskAiMessage,
    },
  ] = useMutation<
    CreateAskAiMessageMutationMutation,
    CreateAskAiMessageMutationMutationVariables
  >(createAskAiMessageMutation);

  const hasPostedMessage = calledCreateAskAiMessage || calledRetryAskAiMessage;

  const { data, loading } = useQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAIChatSessionQuery, {
    variables: { chatSessionId: chatSessionId || 0 },
    skip: !chatSessionId,
    fetchPolicy: "cache-and-network",
  });
  const chatSession = data?.chatSession;

  const systemContextMessage = useMemo(() => {
    if (!currentSuggestedAssessmentResponseId) {
      return undefined;
    }
    return `User is currently looking at suggested response with id \`${currentSuggestedAssessmentResponseId}\``;
  }, [currentSuggestedAssessmentResponseId]);

  useEffect(() => {
    hasUsedSystemContextMessageRef.current = false;
    pendingSystemContextMessageRef.current = false;
  }, [chatSessionId]);

  const messages = useMemo(
    () =>
      !chatSession && inputMessageWhileCreatingSession
        ? [createChatMessageNode({ content: inputMessageWhileCreatingSession })]
        : chatSession?.messages
          ? assertEdgesNonNullWithStringId(chatSession.messages)
          : [],
    [chatSession, inputMessageWhileCreatingSession],
  );

  const lastMessage = last(messages);

  const sendMessage = useCallback(
    (content: string) => {
      const newMessageNode = createChatMessageNode({ content });

      // If no chat session, set the fake input message while creating session
      if (!chatSession) {
        setInputMessageWhileCreatingSession(content);
      }
      const shouldIncludeSystemContextMessage =
        !!systemContextMessage &&
        !hasUsedSystemContextMessageRef.current &&
        !pendingSystemContextMessageRef.current;

      if (shouldIncludeSystemContextMessage) {
        pendingSystemContextMessageRef.current = true;
      }

      createAskAIMessage({
        variables: {
          chatSessionId: chatSession?.id,
          meetingId,
          assessmentId,
          assessmentQuestionId,
          message: content,
          systemContextMessage: shouldIncludeSystemContextMessage
            ? systemContextMessage
            : undefined,
        },
        optimisticResponse: chatSession
          ? {
              __typename: "Mutation",
              sendMessageToChatbot: {
                __typename: "SendMessageToChatbotMutation",
                chatSession: {
                  ...chatSession,
                  updated: new Date().toISOString(),
                  messages: {
                    __typename: "ChatMessageConnection",
                    edges: [
                      ...chatSession.messages.edges,
                      {
                        __typename: "ChatMessageEdge",
                        node: newMessageNode,
                      },
                    ],
                  },
                },
              },
            }
          : undefined,
        onCompleted: (response) => {
          if (shouldIncludeSystemContextMessage) {
            hasUsedSystemContextMessageRef.current = true;
            pendingSystemContextMessageRef.current = false;
          }
          const newChatSession = response?.sendMessageToChatbot?.chatSession;
          if (!chatSession && newChatSession) {
            // Update cache for chat session and messages
            // so when this component is re-rendered with chatSessionId
            // it renders right away the messages
            cache.writeQuery<
              GetAskAiChatSessionQueryQuery,
              GetAskAiChatSessionQueryQueryVariables
            >({
              query: getAskAIChatSessionQuery,
              variables: { chatSessionId: newChatSession.id },
              data: {
                __typename: "Query",
                chatSession: newChatSession,
              },
            });

            // Update chat session id
            onChangeChatSessionId(newChatSession.id || null);

            // Update chat history
            const query = getWatchedQuery(getAskAiChatSessionHistoryQuery);
            if (query) query.refetch();
          }
        },
        onError: () => {
          if (shouldIncludeSystemContextMessage) {
            pendingSystemContextMessageRef.current = false;
          }
          setInputMessage(content);
          defer(() => {
            editorRef.current?.commands.setContent(content);
          });
        },
      });
    },
    [
      chatSession,
      meetingId,
      assessmentId,
      assessmentQuestionId,
      onChangeChatSessionId,
      createAskAIMessage,
      systemContextMessage,
    ],
  );

  const clearInput = () => {
    setInputMessage("");
    defer(() => {
      editorRef.current?.commands.setContent("");
    });
  };

  const handleSubmitMessage = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (loadingCreateMessage || loadingRetryAskAiMessage) return;
      if (inputMessage.trim().length === 0) {
        return;
      }
      onStartChatSession();
      sendMessage(inputMessage);
      clearInput();
    },
    [
      inputMessage,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      onStartChatSession,
      sendMessage,
    ],
  );

  const handleChangeInputValue = useCallback(
    (value: string) => {
      setInputMessage(value);
    },
    [setInputMessage],
  );

  const handleClickPromptSuggestion = useCallback(
    (prompt: string) => {
      onStartChatSession();
      sendMessage(prompt);
    },
    [sendMessage, onStartChatSession],
  );

  const filteredMessages = useMemo(() => {
    return (
      messages.filter(
        ({ autogenerated, role, id, error }) =>
          !autogenerated &&
          role !== ChatbotRoleEnum.System &&
          // only show error message if it is the last message
          (!error || (error && id === lastMessage?.id)),
      ) || []
    );
  }, [messages, lastMessage]);

  const messageLength = useMemo(
    () => filteredMessages.length,
    [filteredMessages],
  );
  const hasMessages = useMemo(
    () => filteredMessages.length > 0,
    [filteredMessages],
  );

  const hasNoMessages = useMemo(
    () => filteredMessages.length === 0,
    [filteredMessages],
  );

  const lastMessageIsFromAssistant = useMemo(
    () => !!lastMessage && lastMessage.role === ChatbotRoleEnum.Assistant,
    [lastMessage],
  );
  const lastMessageIsFromUser = useMemo(
    () => !!lastMessage && lastMessage.role === ChatbotRoleEnum.User,
    [lastMessage],
  );

  const lastMessageFromUser = useMemo(
    () =>
      filteredMessages.findLast(
        (message) => message.role === ChatbotRoleEnum.User,
      ),
    [filteredMessages],
  );

  const waitingForSuggestedAssessmentResponse = useMemo(() => {
    const hasOnlySystemMessages = messages.every(
      (message) => message.role === ChatbotRoleEnum.System,
    );
    const isForAssessmentQuestion =
      !!chatSession?.assessment && !!chatSession.assessmentQuestion;
    return hasOnlySystemMessages && isForAssessmentQuestion;
  }, [messages, chatSession]);

  const lastUpdate = useMemo(() => {
    return chatSession?.updated;
  }, [chatSession]);

  const showLoadingUI = useMemo(() => {
    return (
      waitingForSuggestedAssessmentResponse ||
      loadingCreateMessage ||
      loadingRetryAskAiMessage ||
      (!hasNoMessages && !lastMessageIsFromAssistant)
    );
  }, [
    waitingForSuggestedAssessmentResponse,
    loadingCreateMessage,
    loadingRetryAskAiMessage,
    hasNoMessages,
    lastMessageIsFromAssistant,
  ]);

  const reactionsByMessageId = useMemo(() => {
    const reactions = data?.chatSession?.reactions
      ? assertEdgesNonNull(data.chatSession.reactions)
      : [];
    return keyBy(reactions, "messageId");
  }, [data]);

  const handleClickScrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current?.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (
      lastMessage?.role === ChatbotRoleEnum.User &&
      lastUserMessageRef.current
    ) {
      lastUserMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [lastMessage]);

  // Scroll to bottom for assistant messages
  useEffect(() => {
    if (
      hasMessages &&
      lastMessageIsFromAssistant &&
      scrollRef.current &&
      !hasPostedMessage
    ) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [hasMessages]); // only scroll to bottom when when we are loading the messages

  const [canClickScrollToBottom, setCanClickScrollToBottom] = useState(false);

  // Debounce handleCheckIfCanClickScrollButton to avoid excessive calls on scroll
  const handleCheckIfCanClickScrollButton = useCallback(() => {
    const scrollTop = scrollRef.current?.scrollTop || 0;
    const scrollHeight = scrollRef.current?.scrollHeight || 0;
    const clientHeight = scrollRef.current?.clientHeight || 0;
    const canScrollToBottom = scrollTop + clientHeight < scrollHeight - 100;
    setCanClickScrollToBottom(canScrollToBottom);
  }, []);

  const throttleCheckIfCanClickScrollButton = useMemo(
    () => throttle(handleCheckIfCanClickScrollButton, 1000),
    [handleCheckIfCanClickScrollButton],
  );

  useEffect(() => {
    // Cleanup debounce on unmount
    return () => {
      throttleCheckIfCanClickScrollButton.cancel?.();
    };
  }, [throttleCheckIfCanClickScrollButton]);

  useEffect(() => {
    if (lastMessage?.role === ChatbotRoleEnum.Assistant) {
      throttleCheckIfCanClickScrollButton();
    }
  }, [lastMessage, handleCheckIfCanClickScrollButton]);

  useEffect(() => {
    const div = scrollRef.current;
    if (div) {
      div.addEventListener("scroll", throttleCheckIfCanClickScrollButton, {
        passive: true,
      });
      throttleCheckIfCanClickScrollButton();
    }
    return () => {
      if (div) {
        div.removeEventListener("scroll", throttleCheckIfCanClickScrollButton);
      }
    };
  }, [messageLength]);

  const handleReSendMessage = useCallback(() => {
    handleSubmitMessage();
    // we re-create the chat session if there is no chat session id
    // sendMessage(inputMessage);
    setRetryCount((val) => val + 1);
  }, [handleSubmitMessage]);

  const handleRetryLastMessage = useCallback(() => {
    // we re-create the chat session if there is no chat session id
    if (!chatSessionId) {
      handleReSendMessage();
    } else if (waitingForSuggestedAssessmentResponse || lastMessageIsFromUser) {
      // we retry the last message if it is from the user
      retryAskAiMessage({ variables: { chatSessionId } });
      setRetryCount((val) => val + 1);
    }
  }, [
    waitingForSuggestedAssessmentResponse,
    lastMessageIsFromUser,
    chatSessionId,
    retryAskAiMessage,
    handleReSendMessage,
  ]);

  // Clear input when the url changes
  useEffect(() => {
    clearInput();
    setInputMessageWhileCreatingSession("");
  }, [location.search, location.pathname]);

  // Clear function call when the messages change
  useEffect(() => {
    chatSessionLoadingTextVar(null);
  }, [loading, messages.length]);

  const functionCallLabel: null | string = useMemo(() => {
    return chatSessionLoadingText?.text || null;
  }, [chatSessionLoadingText]);

  const label = useLabel();
  const heightOfFormAndLastMessage = lastMessageIsFromAssistant
    ? "270px"
    : "200px";

  // If not full screen, we just render the form
  return !isFullScreen ? (
    <div className={classNames("fs-unmask", className)}>
      <AskAIForm
        onSubmit={handleSubmitMessage}
        chatSessionId={chatSessionId}
        formButtons={formButtons}
        inputMessage={inputMessage}
        loadingCreateMessage={loadingCreateMessage}
        onClickChatSessionId={onChangeChatSessionId}
        loadingRetryAskAiMessage={loadingRetryAskAiMessage}
        onClickPromptSuggestion={handleClickPromptSuggestion}
        onChangeInputValue={handleChangeInputValue}
        autoFocus={false}
        onEditorReady={(editor) => {
          editorRef.current = editor;
        }}
      />
      <AskAIEmptyPrompts
        isFullScreen={isFullScreen}
        onStartChatSessionWithPrompt={handleClickPromptSuggestion}
        key="to-prevent-re-render-of-random-prompts"
      />
    </div>
  ) : (
    <div className={classNames("flex-1 flex flex-col fs-unmask", className)}>
      <div className="flex-1 relative">
        {waitingForSuggestedAssessmentResponse ? (
          <div className="pt-6">
            <AskAILoading
              initialLabel="Generating suggested responses..."
              key={retryCount}
              lastUpdate={lastUpdate}
              onClickRetry={handleRetryLastMessage}
            />
          </div>
        ) : hasNoMessages && !loading ? (
          <div className="flex flex-col h-full">
            <div className="flex-1" />
            {assessmentId ? (
              <div className="text-gray-800 pt-6">
                <div className="text-base leading-4 mb-3 font-medium flex items-center gap-2">
                  It looks like you're completing a {label("review")}!
                  <Tooltip text="Context generation is a new feature currently in beta. We are actively improving the experience!">
                    <div
                      data-state="In progress"
                      className="px-2 py-0.5 bg-blue-800 rounded-[14px] text-white text-xs"
                    >
                      Beta
                    </div>
                  </Tooltip>
                </div>
                <div className="text-sm leading-5 mb-10">
                  Click the context button to get guidance on a{" "}
                  {label("review")} question. We use data from Topicflow and
                  other integrations as sources, only visible to the responder.
                  When delivered, viewers will only see sources they have access
                  to.
                </div>
              </div>
            ) : !isInSidebar ? (
              <AskAIEmptyPrompts
                onStartChatSessionWithPrompt={handleClickPromptSuggestion}
                isFullScreen={isFullScreen}
                key="to-prevent-re-render-of-random-prompts"
              />
            ) : null}
          </div>
        ) : (
          <div
            className="absolute top-0 bottom-0 left-0 right-0 overflow-y-auto flex flex-col"
            ref={scrollRef}
            id="ask-ai-scroll-container"
          >
            <div
              className={classNames(
                "pt-10 pb-8 flex flex-col w-full max-w-[900px] mx-auto",
                isInSidebar ? "gap-8" : "gap-10",
              )}
            >
              {filteredMessages.map((message, index) => (
                <div
                  // don't use key=message.id to otherwise it'll re-render the loading UI.
                  // AskAIMessage has the key=message.id so it'll re-render when the id is changed.
                  key={index}
                  style={{
                    minHeight:
                      hasPostedMessage && index === filteredMessages.length - 1
                        ? `calc(100vh - ${heightOfFormAndLastMessage} - ${totalYPaddingAroundContainer}px)`
                        : undefined,
                  }}
                >
                  <AskAIMessage
                    message={message}
                    key={message.id}
                    chatSessionId={chatSessionId}
                    reaction={reactionsByMessageId[message.id] || null}
                    isLastMessage={message.id === lastMessage?.id}
                    ref={
                      message.id === lastMessageFromUser?.id
                        ? lastUserMessageRef
                        : undefined
                    }
                    onClickPromptSuggestion={handleClickPromptSuggestion}
                  />

                  {message.id === lastMessage?.id && (
                    <div
                      className={classNames(
                        "flex flex-col gap-10 min-h-[40px] empty:hidden",
                        errorCreateMessage || showLoadingUI || functionCallLabel
                          ? "mt-10"
                          : "mt-6",
                      )}
                      id="status-container"
                    >
                      {errorCreateMessage ? (
                        <AskAIErrorMessage
                          key={retryCount}
                          onClickRetry={handleReSendMessage}
                        />
                      ) : showLoadingUI || functionCallLabel ? (
                        <AskAILoading
                          initialLabel={functionCallLabel || "Thinking..."}
                          key={retryCount}
                          lastUpdate={lastUpdate}
                          onClickRetry={handleRetryLastMessage}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="relative shrink-0 h-px">
        {hasMessages && canClickScrollToBottom && (
          <button
            type="button"
            onClick={handleClickScrollToBottom}
            className="bg-white absolute left-1/2 -translate-x-1/2 -top-10 rounded-full p-1.5 text-gray-600 border hover:bg-gray-100"
            aria-label="Scroll to bottom"
          >
            <TopicflowIcon type="downArrow" className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="w-full max-w-[900px] mx-auto">
        <AskAIForm
          onSubmit={handleSubmitMessage}
          chatSessionId={chatSessionId}
          formButtons={formButtons}
          inputMessage={inputMessage}
          loadingCreateMessage={loadingCreateMessage}
          loadingRetryAskAiMessage={loadingRetryAskAiMessage}
          onClickPromptSuggestion={handleClickPromptSuggestion}
          onChangeInputValue={handleChangeInputValue}
          onEditorReady={(editor) => {
            editorRef.current = editor;
          }}
          autoFocus={true}
          onClickChatSessionId={onChangeChatSessionId}
        />
      </div>
    </div>
  );
};

export default AskAI;
